import React from 'react';
import PropTypes from 'prop-types';
import Icon from 'react-svg-ikon';
import { NavLink } from 'react-router-dom';
import withHandlers from 'recompose/withHandlers';
import { css, StyleSheet } from 'aphrodite';
import { stylePropType, childrenPropType, TSS_BLACK, TSS_DARK_BLUE, TSS_BLUE, hexgen } from 'tss-core-lib-common';
import { IMG_ICON_CHEVRON_RIGHT, IMG_ICON_CHEVRON_DOWN_SMALL, IMG_ICON_CHEVRON_UP_SMALL } from '../../img/icons';
import Cell from './Cell';
import Checkbox from '../Checkbox';
import Tooltip from '../Tooltip';

import { ColumnShape, DataRowShape } from './TableCommon';

var styles = void 0;

var enhanceRow = withHandlers({
  onSelectChange: function onSelectChange(props) {
    return function (_, checked, event) {
      return props.onSelectChange(props.id, checked, event, 'appapa');
    };
  },
  onClick: function onClick(props) {
    return function () {
      return props.onExpandChange && props.onExpandChange(props.id, !props.rowData.expanded);
    };
  }
});

var Row = function Row(_ref) {
  var id = _ref.id,
      children = _ref.children,
      style = _ref.style,
      columns = _ref.columns,
      rowData = _ref.rowData,
      expandable = _ref.expandable,
      selectable = _ref.selectable,
      showSelectCell = _ref.showSelectCell,
      rowDisabled = _ref.rowDisabled,
      rowDisabledReason = _ref.rowDisabledReason,
      onSelectChange = _ref.onSelectChange,
      onClick = _ref.onClick;

  var content = columns.map(function (column, i) {
    return React.createElement(Cell, Object.assign({ key: hexgen() }, column, { id: id, data: rowData.data[i], style: rowDisabled && styles.disabledRow }));
  });

  var checked = function checked(row) {
    if (row.disabled) {
      return false;
    }
    return row.data[row.data.length - 1] === true;
  };
  var isUrlFunction = rowData.url instanceof Function;

  if (showSelectCell) {
    content.push(React.createElement(
      'div',
      { key: 'select', className: css(styles.selectContainer) },
      selectable === true ? React.createElement(Checkbox, {
        disabled: rowData.disabled,
        checked: checked(rowData),
        onChange: isUrlFunction ? null : onSelectChange
      }) : null
    ));
  }

  if (expandable) {
    content.push(React.createElement(
      'div',
      { key: 'expand', className: css(styles.navArrowContainer) },
      React.createElement(Icon, {
        key: rowData.expanded ? 'expanded' : 'minimized',
        src: rowData.expanded ? IMG_ICON_CHEVRON_UP_SMALL : IMG_ICON_CHEVRON_DOWN_SMALL,
        stroke: TSS_BLACK()
      })
    ));
  }
  if (isUrlFunction && !rowDisabled) {
    content = React.createElement(
      'div',
      {
        className: css(styles.row, styles.rowLink, style),
        role: 'button',
        tabIndex: 0,
        onClick: function onClick() {
          return rowData.url(id);
        }
      },
      content
    );
  } else if (isUrlFunction && rowDisabled) {
    content = React.createElement(
      'div',
      {
        className: css(styles.row, styles.rowLink, style),
        role: 'button',
        tabIndex: 0
      },
      content,
      rowDisabledReason ? React.createElement(
        Tooltip,
        null,
        rowDisabledReason
      ) : null
    );
  } else if (rowData.url && !rowDisabled) {
    content = React.createElement(
      NavLink,
      {
        to: rowData.url,
        className: css(styles.row, styles.rowLink, style)
      },
      content,
      React.createElement(
        'div',
        { className: css(styles.navArrowContainer) },
        React.createElement(Icon, { src: IMG_ICON_CHEVRON_RIGHT, stroke: TSS_BLACK() })
      )
    );
  } else {
    content = React.createElement(
      'div',
      {
        className: css(styles.row, style),
        role: 'button',
        tabIndex: 0,
        onClick: onClick
      },
      content,
      rowDisabled ? React.createElement(
        React.Fragment,
        null,
        React.createElement(
          'div',
          { className: css(styles.navArrowContainer) },
          React.createElement(Icon, { src: IMG_ICON_CHEVRON_RIGHT, stroke: TSS_BLACK(0.3) })
        ),
        rowDisabledReason ? React.createElement(
          Tooltip,
          null,
          rowDisabledReason
        ) : null
      ) : null
    );
  }

  return React.createElement(
    'div',
    {
      className: css(styles.rowWrapper, expandable && styles.expandableRowWrapper)
    },
    content,
    children ? React.createElement(
      'div',
      { className: css(styles.rowChildren) },
      children
    ) : null
  );
};

Row.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  children: childrenPropType,
  style: stylePropType,
  columns: PropTypes.arrayOf(ColumnShape).isRequired,
  rowData: DataRowShape.isRequired,
  showSelectCell: PropTypes.bool,
  expandable: PropTypes.bool,
  selectable: PropTypes.bool,
  rowDisabled: PropTypes.bool,
  rowDisabledReason: PropTypes.string,
  onSelectChange: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired
};

Row.defaultProps = {
  children: null,
  style: null,
  expandable: false,
  selectable: false,
  showSelectCell: false,
  rowDisabled: false,
  rowDisabledReason: null
};

styles = StyleSheet.create({
  rowWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    borderBottom: '2px solid ' + TSS_DARK_BLUE(0.05)
  },

  rowChildren: {
    borderTop: '1px solid ' + TSS_DARK_BLUE(0.05)
  },

  expandableRowWrapper: {
    cursor: 'pointer'
  },

  row: {
    backgroundColor: 'white',
    display: 'flex',
    color: TSS_BLACK(),
    outline: 0
  },
  disabledRow: {
    color: TSS_BLACK(0.3)
  },
  rowLink: {
    textDecoration: 'none',
    color: TSS_BLACK(),
    cursor: 'pointer',
    ':active': {
      backgroundColor: TSS_BLUE(0.15)
    }
  },

  navArrowContainer: {
    width: '4rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '0 0 0 1rem'
  },

  selectContainer: {
    width: '5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '0 0 0 1rem'
  }
});

export default enhanceRow(Row);